import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PropertySetInputValue } from '@vsolv/packages/properties/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { Provisioning } from '@wsphere/warranties/domain';
import { filter, firstValueFrom, map, Subject, takeUntil } from 'rxjs';
import { RatingMatrixService } from '../../../policy/services/rating-matrix.service';
import { ProvisioningSessionService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-provision-confirmation',
  templateUrl: './provision-confirmation.component.html',
  standalone: false,
})
export class ProvisionConfirmationComponent implements OnInit, OnDestroy {
  constructor(
    private sessionSvc: ProvisioningSessionService,
    private route: ActivatedRoute,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private toastSvc: ToastService,
    private ratingSvc: RatingMatrixService
  ) {}

  @HostBinding('class') _class = 'pb-24 px-6';

  protected stepCompleted = false;
  protected destroy$ = new Subject<void>();

  isLargeScreen$ = this.breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(map(state => !state.matches));

  protected session$ = this.sessionSvc.getCurrentSession();

  protected assetInfo = new FormControl(
    null as { propertyValues: Record<string, unknown>; dimensionValues: Record<string, unknown> } | null
  );

  readonly staffView$ = this.route.data.pipe(map(data => data['staffView'] as boolean));

  private navigateTo(path?: string) {
    this.router.navigate([path ?? `..`], {
      relativeTo: this.route,
    });
  }

  previousPage(staffView: boolean) {
    if (staffView) this.navigateTo('/provisioning/policy');
    else this.navigateTo('/checkout/policy');
  }

  async nextPage(session: Provisioning.ProvisioningSession | null, staffView: boolean) {
    const assetInfo = this.assetInfo.value as PropertySetInputValue | null;
    if (!session?.policy || !assetInfo || !this.assetInfo.valid) {
      this.toastSvc.show({
        type: 'error',
        title: 'Cannot save asset information',
        text: 'Please provide all required information before continuing',
      });
      return;
    }

    if (staffView) {
      await this.ratingSvc.syncVehicleMakeModelElements(assetInfo.propertyValues);
    }

    await this.sessionSvc.updateSession({
      sessionId: session.id,
      data: {
        assetDraft: {
          propertySetId: session.policy.propertySetId,
          propertyValues: assetInfo.propertyValues,
          dimensionValues: assetInfo.dimensionValues,
        },
      },
    });

    if (staffView) {
      this.navigateTo('/provisioning/plan');
    } else {
      this.navigateTo('/checkout/plan');
    }
  }

  ngOnInit(): void {
    this.assetInfo.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(assetInfo => (this.stepCompleted = !!assetInfo));

    // Pre-populate the unsaved values when returning to this step.
    firstValueFrom(
      this.session$.pipe(filter((session): session is Provisioning.ProvisioningSession => !!session))
    ).then(session => {
      if (!session.assetDraft || this.assetInfo.value) return;
      this.assetInfo.setValue({
        propertyValues: session.assetDraft.propertyValues,
        dimensionValues: session.assetDraft.dimensionValues ?? {},
      });
      this.stepCompleted = true;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
